import { colors } from '@client/new/components/colors';
import { Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { checkAcceptedFileTypes } from '@client/new/helpers/file';

export type DropZoneProps = {
  onChange: (file: File) => void;
  infoText?: string;
  accept?: string[];
  onlySingleFile?: boolean;
  onError?: (message: string) => void;
};

export const DropZone = (p: DropZoneProps) => {
  const [dragOver, setDragOver] = useState(false);
  const inputFile = useRef<HTMLInputElement | null>(null);

  const onFile = (f: File) => {
    if (checkAcceptedFileTypes(f, p.accept)) p.onChange(f);
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    onFile(e.target.files[0]);
    // must happen after we get the file from the input
    if (inputFile.current) inputFile.current.value = '';
  };

  const onDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(true);
  };

  const onDragEnd = () => setDragOver(false);

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files.length === 0) return;
    if (p.onlySingleFile && e.dataTransfer.files.length > 1) {
      p.onError && p.onError('You can drop only one file.');
      return;
    }
    if (p.accept && !p.accept.includes(e.dataTransfer.files[0].type)) {
      p.onError && p.onError('This file type is not accepted.');
      return;
    }
    onFile(e.dataTransfer.files[0]);
  };

  return (
    <div
      onDragOverCapture={onDragStart}
      onDragLeaveCapture={onDragEnd}
      onClick={() => inputFile.current?.click()}
      className="cursor-pointer text-center flex flex-col gap-4 py-12 px-6 justify-center items-center border-2 border-dashed rounded-3xl"
      style={{
        background: dragOver ? colors.secondary200 : 'white',
        borderColor: dragOver ? colors.secondary900 : colors.inactive,
      }}
      onDropCapture={onDrop}
    >
      <input
        style={{ display: 'none' }}
        ref={inputFile}
        type="file"
        onChange={onInputChange}
        accept={p.accept?.join(', ')}
      />

      <Typography variant="h5">
        Click to browse or <br /> drag and drop your files
      </Typography>

      {p.infoText && (
        <Typography variant="subtitle2" sx={{ color: colors.text600 }}>
          {p.infoText}
        </Typography>
      )}
    </div>
  );
};
